import React, { useMemo } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { Form } from 'react-bootstrap'
import Select from 'components/FormUtils/Select'
import { DatePicker } from 'components/FormUtils/DatePicker'
import apiService from 'services/api'

const ErrorText = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => <div className={`text-red-600 text-sm mt-1 ${className}`}>{children}</div>

export function OrderInfoStep() {
  const {
    control,
    formState: { errors },
  } = useFormContext()

  const api = apiService()
  const { data: distributors, isLoading: isLoadingDistributors } =
    api.useGetDistributors()

  // Fulfillment option options
  const fulfillmentOptions = useMemo(
    () => [
      { label: 'Ship to Address', value: 'ship_to_address' },
      { label: 'Pick Up Locally', value: 'pick_up_locally' },
      {
        label: 'Ship with Existing Request',
        value: 'ship_with_existing_order',
      },
    ],
    []
  )

  // Distributor options for dropdown
  const distributorOptions = useMemo(() => {
    if (!distributors) return []
    return distributors.map((dist) => ({
      label: dist.name,
      value: dist.id,
    }))
  }, [distributors])

  return (
    <div>
      <h5 className="text-lg font-medium mb-4">Request Information</h5>

      <div>
        <Controller
          name="date_needed"
          control={control}
          render={({ field }) => (
            <>
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                label="Date Needed *"
                className="mb-4"
              />
              {errors.date_needed && (
                <ErrorText>{errors.date_needed.message as string}</ErrorText>
              )}
            </>
          )}
        />
      </div>

      <div className="flex items-start gap-2 mb-4">
        <div className="flex-1">
          <Select
            control={control}
            name="fulfillment_option"
            label="Fulfillment Option"
            options={fulfillmentOptions}
            placeholder="Select fulfillment option"
            isMulti={false}
          />
        </div>

        <div className="flex-1">
          <Select
            control={control}
            name="distributor_id"
            label="Distributor"
            optional
            options={distributorOptions}
            placeholder={
              isLoadingDistributors
                ? 'Loading distributors...'
                : 'Select distributor'
            }
            isMulti={false}
            isClearable
            disabled={isLoadingDistributors}
          />
        </div>
      </div>

      <div className="mt-4">
        <Form.Label className="font-semibold mb-2">
          Special Instructions{' '}
          <span className="text-gray-500 font-normal">(optional)</span>
        </Form.Label>
        <Controller
          name="special_instructions"
          control={control}
          render={({ field }) => (
            <Form.Control
              as="textarea"
              rows={4}
              {...field}
              placeholder="Any special instructions or notes for this request..."
            />
          )}
        />
      </div>
    </div>
  )
}
